import { createSelector } from '@reduxjs/toolkit';
import { getOfferWithMinPricingSummary, mapCancellationTypeByCancellation } from 'gql/service/graph/activityService';
import { CancellationType } from 'gql/autogen/CancellationType';
import { CmsValue, Experiments } from 'core/type/BootstrapConfig.types';
import { selectConfigFromBootstrap, selectPrerenderStateFromBootstrap } from 'store/slice/bootstrap';
import { selectIsCalendarLoading, selectIsSoldOut } from 'component/page/detail/store/calendar.selectors';
import { selectActivityRepresentativeInfo } from 'component/page/detail/store/detail.selectors';
import { PriceDisplayBehavior } from 'core/type/behaviors.types';
import { navigateToHashWithTabBarOffset } from 'component/common/DetailInViewWrapper/navigateToHashWithTabBarOffset';
import { selectAvailabilityIsLoading, selectCheapestAvailableOffer } from 'component/page/detail/store/availabilitySelectorIndex';
import { NavigationHashEnum } from 'core/type/Details.types';
import { PricingSummary } from 'gql/autogen/PricingSummary';
import { RepresentativeInfo } from 'gql/autogen/RepresentativeInfo';
import { Offer } from 'gql/autogen/Offer';
import { setIsScrolling } from 'component/page/detail/store/details';
import { selectTabs } from 'component/page/detail/DetailPageTabs/DetailPageTabs.selectors';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { focusElementByDataElementNameSelector } from 'core/utils/domUtils';

interface StateProps {
    cms: CmsValue;
    cancellationMessage?: string;
    isDisabled: boolean;
    isLoading: boolean;
    isSchedulesEmpty: boolean;
    showPriceWithPointsOnNextLine: boolean;
    pricingSummary?: PricingSummary;
    cheapestPricingSummary?: PricingSummary;
    packageOptionsHref: string;
    isPrerender: boolean;
    showInclusiveTax: boolean;
    isImproveUnavailableOffer: boolean;
    experiments: Experiments;
    activityId: number;
}

export interface DispatchProps {
    onSeeOptionsButtonClick: (packageOptionsHref: string) => void;
}

export type ActivityDetailsFooterProps = StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectAvailabilityIsLoading,
    selectIsCalendarLoading,
    selectIsSoldOut,
    selectTabs,
    selectPrerenderStateFromBootstrap,
    selectActivityRepresentativeInfo,
    selectCheapestAvailableOffer,
    (config, isAvailabilityLoading, isCalendarLoading, isSchedulesEmpty, tabsObject, isPrerender, representativeInfo?: RepresentativeInfo, cheapestOffer?: Offer): StateProps => {
        const cms = config.cms;
        const isLoading = isCalendarLoading || isAvailabilityLoading;
        const cancellationType = mapCancellationTypeByCancellation(representativeInfo?.cancellationPolicy);
        const pricingSummary = representativeInfo?.pricingSummary;
        const { tabs } = tabsObject;
        const packageOptionsTab = tabs.find((tab) => tab.id === NavigationHashEnum.PackageOptionsSection) || { href: '' };
        const hideFreeCancellation = config?.behavior?.common?.hideFreeCancellation;
        const showInclusiveTax = config.behavior.common.showInclusiveTax;
        const isImproveUnavailableOffer = isOn(FeatureName.ImproveUnavailableOffer, config);
        const activityId = representativeInfo?.activityId || cheapestOffer?.activityId || 0;
        return {
            cms,
            isLoading,
            isSchedulesEmpty,
            pricingSummary,
            isPrerender,
            activityId,
            cheapestPricingSummary: getOfferWithMinPricingSummary(cheapestOffer?.enrichedOfferOption || [])?.pricingSummary,
            cancellationMessage: cancellationType === CancellationType.FREE_CANCELLATION && !hideFreeCancellation ? config.cms[144284] : undefined,
            isDisabled: !isImproveUnavailableOffer && isSchedulesEmpty && !isLoading,
            showPriceWithPointsOnNextLine: config.behavior.common.price.displayBehavior === PriceDisplayBehavior.PriceWithPointsOnNextLine,
            packageOptionsHref: packageOptionsTab.href,
            showInclusiveTax,
            isImproveUnavailableOffer,
            experiments: config.experiments,
        };
    },
);

export const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
    onSeeOptionsButtonClick: (packageOptionsHref: string) => {
        navigateToHashWithTabBarOffset(dispatch, `#${packageOptionsHref}`, setIsScrolling, true);
        const isFocusSuccess = focusElementByDataElementNameSelector('activities-available-option-card-choose-button');
        if (!isFocusSuccess) {
            focusElementByDataElementNameSelector('activities-details-package-option-change-date');
        }
    },
});
